import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useUpdateTenant, useUploadTenantLogo } from "../../lib/api";
import { FieldLabel, GhostButton, SecondaryButton, initials } from "./primitives";

// The brand's logo, shown in the dashboard header and this console's brand
// switcher. Falls back to the initials tile when a brand has none.
export default function BrandLogoEditor({ tenant }) {
  const upload = useUploadTenantLogo(tenant.id);
  const update = useUpdateTenant();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const logo = preview || tenant.logo_url || "";

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    try {
      await upload.mutateAsync(file);
      toast.success("Logo updated.");
    } catch (err) {
      setPreview(null);
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
  };

  const onRemove = async () => {
    try {
      await update.mutateAsync({ id: tenant.id, logo_url: "" });
      setPreview(null);
      toast.success("Logo removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove the logo.");
    }
  };

  const busy = upload.isPending || update.isPending;

  return (
    <div className="mt-7 max-w-[560px]">
      <FieldLabel>Logo</FieldLabel>
      <div className="flex items-center gap-4">
        <span className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-2xl bg-[#F9FAFB] shadow-[0_0_0_1px_#E5E7EB]">
          {logo ? (
            <img src={logo} alt={tenant.name} className="h-full w-full object-contain" />
          ) : (
            <span className="text-[16px] font-semibold text-[#6B7280]">{initials(tenant.name)}</span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={onPick} className="hidden" data-testid="brand-logo-input" />
          <SecondaryButton onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy && <Loader2 className="animate-spin" size={14} />}
            {logo ? "Replace" : "Upload logo"}
          </SecondaryButton>
          {logo && <GhostButton onClick={onRemove} disabled={busy}>Remove</GhostButton>}
        </div>
      </div>
      <p className="mt-2 text-[12px] text-[#6B7280]">PNG, JPG, WEBP or SVG · up to 2 MB</p>
    </div>
  );
}
